import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Employees as EmployeesApi, Contracts, Meta, Fleet, Routes as RoutesAPI } from "../lib/api";
import {
  Briefcase, FileText, Car, ArrowRight, ArrowLeft, Check, UserPlus, Download, Eye, Clock,
  Package,
} from "lucide-react";
import { toast } from "sonner";

const TEMPLATES = [
  {
    id: "employment",
    title: "Umowa o pracę",
    subtitle: "Pełnoetatowe zatrudnienie zgodnie z Kodeksem Pracy.",
    icon: Briefcase,
  },
  {
    id: "mandate",
    title: "Umowa zlecenie",
    subtitle: "Współpraca cywilnoprawna z ewidencją godzin.",
    icon: FileText,
  },
  {
    id: "mandate_per_package",
    title: "Umowa zlecenie od paczki",
    subtitle: "Stawka od paczki + multipack 0,50 zł, 1 miesiąc wypowiedzenia, odpowiedzialność za sprzęt.",
    icon: Package,
  },
  {
    id: "vehicle",
    title: "Umowa powierzenia auta",
    subtitle: "Przekazanie pojazdu służbowego z protokołem stanu.",
    icon: Car,
  },
  {
    id: "hours_statement",
    title: "Wykaz godzin",
    subtitle: "Rozliczenie miesięczne — kwota przeliczana automatycznie na godziny.",
    icon: Clock,
  },
];

const EMPTY_VEHICLE = {
  brand: "",
  model: "",
  registration: "",
  vin: "",
  year: "",
  damages: "",
  equipment: {
    triangle: false,
    extinguisher: false,
    key: false,
    scraper: false,
    charger: false,
    phone_holder: false,
    big_bag: false,
  },
};

const EMPTY_EMPLOYEE = {
  first_name: "",
  last_name: "",
  pesel: "",
  birth_date: "",
  id_document_type: "dowod",
  id_document_series: "",
  id_document_number: "",
  address: "",
  position: "",
  hourly_rate: "",
};

const ID_DOC_TYPES = [
  { value: "dowod", label: "Dowód osobisty" },
  { value: "paszport", label: "Paszport" },
  { value: "karta_pobytu", label: "Karta pobytu" },
];
const ID_DOC_SHORT = {
  dowod: "dow. os.",
  paszport: "paszport",
  karta_pobytu: "karta pob.",
};

export default function NewContract() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [contractType, setContractType] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [newEmployee, setNewEmployee] = useState(null);
  const [equipmentLabels, setEquipmentLabels] = useState({});
  const [fleet, setFleet] = useState([]);
  const [selectedFleetId, setSelectedFleetId] = useState("");
  const [details, setDetails] = useState({
    place: "Szczecin",
    sign_date: new Date().toISOString().slice(0, 10),
    start_date: "",
    end_date: "",
    position: "",
    hourly_rate: "",
    package_rate: "",
    multipack_extra_rate: "",
    period_month: new Date().toISOString().slice(0, 7),
    earned_amount: "",
  });
  const [vehicle, setVehicle] = useState(EMPTY_VEHICLE);
  const [submitting, setSubmitting] = useState(false);
  const [createdContract, setCreatedContract] = useState(null);
  // Global per-package rate configured in /admin/stawki — used as helper default
  // when the admin creates an "Umowa zlecenie od paczki".
  const [globalRates, setGlobalRates] = useState({ env_rate: null, multipack_extra_rate: 0.5 });

  useEffect(() => {
    EmployeesApi.list().then(setEmployees);
    Meta.get().then((m) => setEquipmentLabels(m.equipment));
    Fleet.list().then(setFleet);
    // Pull the current global per-package rate (fallback silently)
    RoutesAPI.listDriverRates()
      .then((d) => setGlobalRates({
        env_rate: d?.env_rate ?? null,
        multipack_extra_rate: d?.multipack_extra_rate ?? 0.5,
      }))
      .catch(() => {});
  }, []);

  const selectedEmployee = useMemo(
    () => employees.find((e) => e.id === selectedEmployeeId),
    [employees, selectedEmployeeId]
  );

  // Pre-fill details when employee changes
  useEffect(() => {
    if (selectedEmployee) {
      setDetails((d) => ({
        ...d,
        position: d.position || selectedEmployee.position || "",
        hourly_rate:
          d.hourly_rate || (selectedEmployee.hourly_rate ?? "").toString(),
      }));
    }
  }, [selectedEmployeeId]); // eslint-disable-line

  const isVehicle = contractType === "vehicle";
  const isHours = contractType === "hours_statement";
  const isPerPackage = contractType === "mandate_per_package";

  const next = () => setStep((s) => Math.min(4, s + 1));
  const back = () => setStep((s) => Math.max(1, s - 1));

  const canProceedStep1 = !!contractType;
  const canProceedStep2 = !!selectedEmployeeId || !!newEmployee;
  const canProceedStep3 = isHours
    ? !!details.period_month && !!details.earned_amount && !!details.hourly_rate
    : isPerPackage
      ? details.start_date && details.package_rate
      : details.start_date && details.end_date && details.hourly_rate;

  const handleCreateEmployee = async () => {
    if (!newEmployee.first_name || !newEmployee.last_name) {
      toast.error("Imię i nazwisko są wymagane");
      return;
    }
    const payload = {
      ...newEmployee,
      hourly_rate:
        newEmployee.hourly_rate === "" ? null : Number(newEmployee.hourly_rate),
    };
    const created = await EmployeesApi.create(payload);
    const list = await EmployeesApi.list();
    setEmployees(list);
    setSelectedEmployeeId(created.id);
    setNewEmployee(null);
    toast.success("Pracownik dodany do bazy");
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      // For hours_statement: compute start/end of month for context
      let start = details.start_date || null;
      let end = details.end_date || null;
      if (isHours && details.period_month) {
        const [y, m] = details.period_month.split("-").map(Number);
        const lastDay = new Date(y, m, 0).getDate();
        start = `${details.period_month}-01`;
        end = `${details.period_month}-${String(lastDay).padStart(2, "0")}`;
      }
      const payload = {
        contract_type: contractType,
        employee_id: selectedEmployeeId,
        place: details.place,
        sign_date: details.sign_date || null,
        start_date: start,
        end_date: end,
        position: details.position,
        hourly_rate: details.hourly_rate ? Number(details.hourly_rate) : null,
        package_rate: isPerPackage && details.package_rate ? Number(details.package_rate) : null,
        multipack_extra_rate: isPerPackage
          ? Number(details.multipack_extra_rate || globalRates.multipack_extra_rate || 0.5)
          : null,
        vehicle: isVehicle
          ? { ...vehicle, year: vehicle.year ? Number(vehicle.year) : null }
          : null,
        period_month: isHours ? details.period_month : null,
        earned_amount: isHours && details.earned_amount ? Number(details.earned_amount) : null,
      };
      const c = await Contracts.create(payload);
      setCreatedContract(c);
      setStep(5);
      toast.success("Dokument wygenerowany");
    } catch (err) {
      toast.error("Błąd: " + (err.response?.data?.detail || err.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-10" data-testid="new-contract-page">
      <div>
        <div className="font-label mb-1">// Generator</div>
        <h1 className="font-heading font-black text-4xl uppercase tracking-tight">
          Nowy dokument
        </h1>
      </div>

      {/* Stepper */}
      <Stepper step={step} />

      {step === 1 && (
        <StepTemplate
          contractType={contractType}
          setContractType={setContractType}
          onNext={next}
          canProceed={canProceedStep1}
        />
      )}

      {step === 2 && (
        <StepEmployee
          employees={employees}
          selectedEmployeeId={selectedEmployeeId}
          setSelectedEmployeeId={(id) => {
            setSelectedEmployeeId(id);
            setNewEmployee(null);
          }}
          newEmployee={newEmployee}
          setNewEmployee={setNewEmployee}
          onCreateEmployee={handleCreateEmployee}
          onBack={back}
          onNext={next}
          canProceed={canProceedStep2}
        />
      )}

      {step === 3 && (
        <StepDetails
          details={details}
          setDetails={setDetails}
          isVehicle={isVehicle}
          isHours={isHours}
          isPerPackage={isPerPackage}
          globalRates={globalRates}
          vehicle={vehicle}
          setVehicle={setVehicle}
          equipmentLabels={equipmentLabels}
          fleet={fleet}
          selectedFleetId={selectedFleetId}
          setSelectedFleetId={(id) => {
            setSelectedFleetId(id);
            if (id) {
              const v = fleet.find((f) => f.id === id);
              if (v) {
                setVehicle((curr) => ({
                  ...curr,
                  brand: v.brand || "",
                  model: v.model || "",
                  registration: v.registration || "",
                  vin: v.vin || "",
                  year: v.year ?? "",
                }));
              }
            }
          }}
          onBack={back}
          onNext={next}
          canProceed={canProceedStep3}
        />
      )}

      {step === 4 && (
        <StepReview
          contractType={contractType}
          employee={selectedEmployee}
          details={details}
          vehicle={vehicle}
          isVehicle={isVehicle}
          isHours={isHours}
          equipmentLabels={equipmentLabels}
          onBack={back}
          onSubmit={submit}
          submitting={submitting}
        />
      )}

      {step === 5 && createdContract && (
        <StepDone
          contract={createdContract}
          onAnother={() => {
            setStep(1);
            setContractType(null);
            setSelectedEmployeeId(null);
            setNewEmployee(null);
            setDetails({
              place: "Szczecin",
              sign_date: new Date().toISOString().slice(0, 10),
              start_date: "",
              end_date: "",
              position: "",
              hourly_rate: "",
            });
            setVehicle(EMPTY_VEHICLE);
            setCreatedContract(null);
          }}
          onHistory={() => navigate("/contracts")}
        />
      )}
    </div>
  );
}

function Stepper({ step }) {
  const steps = [
    { n: 1, label: "Wzór umowy" },
    { n: 2, label: "Pracownik" },
    { n: 3, label: "Szczegóły" },
    { n: 4, label: "Podsumowanie" },
  ];
  return (
    <div className="flex items-stretch border border-zinc-900" data-testid="stepper">
      {steps.map((s, i) => {
        const isActive = step === s.n;
        const isDone = step > s.n;
        return (
          <div
            key={s.n}
            className={`flex-1 px-4 py-3 flex items-center gap-3 border-r border-zinc-900 last:border-r-0 ${
              isActive ? "bg-[#E52C34] text-white" : isDone ? "bg-zinc-900 text-white" : "bg-white text-zinc-900"
            }`}
            data-testid={`step-indicator-${s.n}`}
          >
            <div
              className={`w-7 h-7 flex items-center justify-center font-heading font-black text-sm ${
                isActive || isDone ? "bg-white text-zinc-900" : "bg-zinc-100"
              }`}
            >
              {isDone ? <Check className="w-4 h-4" /> : s.n}
            </div>
            <div className="flex flex-col leading-tight">
              <span className="text-[10px] uppercase tracking-[0.2em] opacity-70">
                Krok {s.n}
              </span>
              <span className="font-bold uppercase text-sm tracking-wider">
                {s.label}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StepTemplate({ contractType, setContractType, onNext, canProceed }) {
  return (
    <section className="space-y-6">
      <h2 className="font-heading font-bold text-xl uppercase">
        Wybierz wzór umowy
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-zinc-900">
        {TEMPLATES.map((t, idx) => {
          const active = contractType === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setContractType(t.id)}
              data-testid={`template-${t.id}`}
              className={`text-left p-8 border-r border-zinc-900 last:border-r-0 transition-colors ${
                active ? "bg-[#E52C34] text-white" : "bg-white hover:bg-zinc-50"
              }`}
            >
              <t.icon className={`w-8 h-8 mb-6 ${active ? "text-white" : "text-[#E52C34]"}`} />
              <div className={`font-label mb-2 ${active ? "text-white/70" : "text-zinc-500"}`}>
                Szablon {idx + 1}
              </div>
              <div className="font-heading font-black text-xl uppercase leading-tight mb-3">
                {t.title}
              </div>
              <div className={`text-sm leading-relaxed ${active ? "text-white/80" : "text-zinc-600"}`}>
                {t.subtitle}
              </div>
            </button>
          );
        })}
      </div>
      <div className="flex justify-end">
        <button
          onClick={onNext}
          disabled={!canProceed}
          data-testid="btn-next-1"
          className="inline-flex items-center gap-2 bg-[#E52C34] text-white px-6 py-3 font-bold uppercase tracking-wider hover:bg-[#C5232A] disabled:bg-zinc-300 disabled:cursor-not-allowed transition-colors"
        >
          Dalej <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </section>
  );
}

function StepEmployee({
  employees, selectedEmployeeId, setSelectedEmployeeId,
  newEmployee, setNewEmployee, onCreateEmployee, onBack, onNext, canProceed,
}) {
  return (
    <section className="space-y-6">
      <h2 className="font-heading font-bold text-xl uppercase">
        Wybierz pracownika
      </h2>

      {!newEmployee && (
        <div className="border border-zinc-900">
          <div className="max-h-[400px] overflow-y-auto" data-testid="employee-picker">
            {employees.length === 0 && (
              <div className="p-8 text-center text-sm text-zinc-500">
                Brak pracowników w bazie. Dodaj pierwszego.
              </div>
            )}
            {employees.map((emp) => {
              const active = selectedEmployeeId === emp.id;
              return (
                <button
                  key={emp.id}
                  type="button"
                  onClick={() => setSelectedEmployeeId(emp.id)}
                  data-testid={`pick-emp-${emp.id}`}
                  className={`w-full text-left px-6 py-4 border-b border-zinc-200 last:border-b-0 flex items-center justify-between transition-colors ${
                    active ? "bg-[#E52C34] text-white" : "bg-white hover:bg-zinc-50"
                  }`}
                >
                  <div>
                    <div className="font-semibold text-base">
                      {emp.first_name} {emp.last_name}
                    </div>
                    <div className={`text-xs mt-1 ${active ? "text-white/70" : "text-zinc-500"}`}>
                      {emp.position || "Brak stanowiska"} • PESEL {emp.pesel || "—"}
                      {emp.birth_date && ` • ur. ${new Date(emp.birth_date).toLocaleDateString("pl-PL")}`}
                    </div>
                    {(emp.id_document_series || emp.id_document_number) && (
                      <div className={`text-[11px] mt-0.5 font-mono ${active ? "text-white/70" : "text-zinc-500"}`}>
                        {ID_DOC_SHORT[emp.id_document_type] || "dok."}{" "}
                        {emp.id_document_series && `${emp.id_document_series} `}
                        {emp.id_document_number}
                      </div>
                    )}
                  </div>
                  {active && <Check className="w-5 h-5" />}
                </button>
              );
            })}
          </div>
          <button
            type="button"
            onClick={() => {
              setNewEmployee(EMPTY_EMPLOYEE);
              setSelectedEmployeeId(null);
            }}
            data-testid="btn-add-new-employee-inline"
            className="w-full px-6 py-4 bg-zinc-900 text-white font-bold uppercase tracking-wider text-sm hover:bg-zinc-800 flex items-center justify-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            Dodaj nowego pracownika
          </button>
        </div>
      )}

      {newEmployee && (
        <div className="border border-zinc-900 p-8 space-y-4 bg-zinc-50" data-testid="inline-new-employee-form">
          <div className="grid grid-cols-2 gap-4">
            <Field label="Imię" required>
              <input
                data-testid="inline-input-first-name"
                className="w-full border border-zinc-300 bg-white px-3 py-2 focus:outline-none focus:border-[#E52C34]"
                value={newEmployee.first_name}
                onChange={(e) => setNewEmployee({ ...newEmployee, first_name: e.target.value })}
              />
            </Field>
            <Field label="Nazwisko" required>
              <input
                data-testid="inline-input-last-name"
                className="w-full border border-zinc-300 bg-white px-3 py-2 focus:outline-none focus:border-[#E52C34]"
                value={newEmployee.last_name}
                onChange={(e) => setNewEmployee({ ...newEmployee, last_name: e.target.value })}
              />
            </Field>
            <Field label="PESEL">
              <input
                data-testid="inline-input-pesel"
                className="w-full border border-zinc-300 bg-white px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
                value={newEmployee.pesel}
                maxLength={11}
                onChange={(e) => setNewEmployee({ ...newEmployee, pesel: e.target.value })}
              />
            </Field>
            <Field label="Data urodzenia">
              <input
                type="date"
                data-testid="inline-input-birth-date"
                className="w-full border border-zinc-300 bg-white px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
                value={newEmployee.birth_date}
                onChange={(e) => setNewEmployee({ ...newEmployee, birth_date: e.target.value })}
              />
            </Field>
            <Field label="Typ dokumentu tożsamości" className="col-span-2">
              <select
                data-testid="inline-input-id-doc-type"
                className="w-full border border-zinc-300 bg-white px-3 py-2 focus:outline-none focus:border-[#E52C34]"
                value={newEmployee.id_document_type}
                onChange={(e) => setNewEmployee({ ...newEmployee, id_document_type: e.target.value })}
              >
                {ID_DOC_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </Field>
            <Field label="Seria dokumentu">
              <input
                data-testid="inline-input-id-doc-series"
                className="w-full border border-zinc-300 bg-white px-3 py-2 font-mono uppercase focus:outline-none focus:border-[#E52C34]"
                value={newEmployee.id_document_series}
                maxLength={5}
                onChange={(e) => setNewEmployee({ ...newEmployee, id_document_series: e.target.value.toUpperCase() })}
                placeholder="ABC"
              />
            </Field>
            <Field label="Numer dokumentu">
              <input
                data-testid="inline-input-id-doc-number"
                className="w-full border border-zinc-300 bg-white px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
                value={newEmployee.id_document_number}
                maxLength={12}
                onChange={(e) => setNewEmployee({ ...newEmployee, id_document_number: e.target.value })}
                placeholder="123456"
              />
            </Field>
            <Field label="Stanowisko">
              <input
                data-testid="inline-input-position"
                className="w-full border border-zinc-300 bg-white px-3 py-2 focus:outline-none focus:border-[#E52C34]"
                value={newEmployee.position}
                onChange={(e) => setNewEmployee({ ...newEmployee, position: e.target.value })}
              />
            </Field>
            <Field label="Adres" className="col-span-2">
              <input
                data-testid="inline-input-address"
                className="w-full border border-zinc-300 bg-white px-3 py-2 focus:outline-none focus:border-[#E52C34]"
                value={newEmployee.address}
                onChange={(e) => setNewEmployee({ ...newEmployee, address: e.target.value })}
              />
            </Field>
            <Field label="Stawka godzinowa (zł)">
              <input
                data-testid="inline-input-hourly-rate"
                type="number"
                step="0.01"
                className="w-full border border-zinc-300 bg-white px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
                value={newEmployee.hourly_rate}
                onChange={(e) => setNewEmployee({ ...newEmployee, hourly_rate: e.target.value })}
              />
            </Field>
          </div>
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onCreateEmployee}
              data-testid="btn-save-inline-employee"
              className="bg-zinc-900 text-white px-5 py-2 font-bold uppercase text-sm tracking-wider hover:bg-zinc-800"
            >
              Zapisz pracownika
            </button>
            <button
              type="button"
              onClick={() => setNewEmployee(null)}
              className="border border-zinc-300 px-5 py-2 font-bold uppercase text-sm tracking-wider hover:border-zinc-900"
            >
              Anuluj
            </button>
          </div>
        </div>
      )}

      <NavButtons onBack={onBack} onNext={onNext} canProceed={canProceed} nextId="btn-next-2" />
    </section>
  );
}

function StepDetails({
  details, setDetails, isVehicle, isHours, isPerPackage, globalRates,
  vehicle, setVehicle,
  equipmentLabels, fleet, selectedFleetId, setSelectedFleetId,
  onBack, onNext, canProceed,
}) {
  // Hours statement form
  if (isHours) {
    const rate = Number(details.hourly_rate) || 0;
    const earned = Number(details.earned_amount) || 0;
    const computedHours = rate > 0 ? earned / rate : 0;
    return (
      <section className="space-y-8">
        <h2 className="font-heading font-bold text-xl uppercase">
          Dane wykazu godzin
        </h2>
        <div className="border border-zinc-900 p-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Miejscowość">
            <input
              data-testid="input-place"
              className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-[#E52C34]"
              value={details.place}
              onChange={(e) => setDetails({ ...details, place: e.target.value })}
            />
          </Field>
          <Field label="Data sporządzenia">
            <input
              data-testid="input-sign-date"
              type="date"
              className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
              value={details.sign_date}
              onChange={(e) => setDetails({ ...details, sign_date: e.target.value })}
            />
          </Field>
          <Field label="Miesiąc rozliczeniowy" required>
            <input
              data-testid="input-period-month"
              type="month"
              className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
              value={details.period_month}
              onChange={(e) => setDetails({ ...details, period_month: e.target.value })}
              required
            />
          </Field>
          <Field label="Stawka godzinowa (zł brutto)" required>
            <input
              data-testid="input-detail-hourly-rate"
              type="number"
              step="0.01"
              className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
              value={details.hourly_rate}
              onChange={(e) => setDetails({ ...details, hourly_rate: e.target.value })}
              required
            />
          </Field>
          <Field label="Zarobiona kwota (zł brutto)" required className="md:col-span-2">
            <input
              data-testid="input-earned-amount"
              type="number"
              step="0.01"
              className="w-full border border-zinc-300 bg-zinc-50 px-4 py-3 text-lg font-mono focus:outline-none focus:border-[#E52C34]"
              value={details.earned_amount}
              onChange={(e) => setDetails({ ...details, earned_amount: e.target.value })}
              placeholder="np. 4500.00"
              required
            />
          </Field>
        </div>

        <div className="border-2 border-brand bg-gradient-to-br from-red-50 to-white p-8 rounded-xl" data-testid="hours-preview">
          <div className="font-label text-brand mb-3">// Wyliczenie automatyczne</div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-center">
            <div>
              <div className="text-xs text-zinc-500 font-mono mb-1">Stawka godzinowa</div>
              <div className="font-heading font-black text-2xl">
                {rate ? `${rate.toFixed(2)} zł` : "—"}
              </div>
            </div>
            <div>
              <div className="text-xs text-zinc-500 font-mono mb-1">Zarobiona kwota</div>
              <div className="font-heading font-black text-2xl">
                {earned ? `${earned.toFixed(2)} zł` : "—"}
              </div>
            </div>
            <div className="bg-brand text-white p-5 rounded-lg">
              <div className="text-xs text-white/70 font-mono mb-1">Liczba godzin</div>
              <div className="font-heading font-black text-3xl" data-testid="computed-hours">
                {computedHours > 0 ? `${computedHours.toFixed(2)} h` : "— h"}
              </div>
            </div>
          </div>
          {rate > 0 && earned > 0 && (
            <div className="text-xs text-zinc-500 font-mono mt-4">
              {earned.toFixed(2)} zł ÷ {rate.toFixed(2)} zł/h = {computedHours.toFixed(2)} h
            </div>
          )}
        </div>

        <NavButtons onBack={onBack} onNext={onNext} canProceed={canProceed} nextId="btn-next-3" />
      </section>
    );
  }

  return (
    <section className="space-y-8">
      <h2 className="font-heading font-bold text-xl uppercase">
        Uzupełnij szczegóły umowy
      </h2>

      <div className="border border-zinc-900 p-8 grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Miejscowość zawarcia">
          <input
            data-testid="input-place"
            className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-[#E52C34]"
            value={details.place}
            onChange={(e) => setDetails({ ...details, place: e.target.value })}
          />
        </Field>
        <Field label="Data podpisania">
          <input
            data-testid="input-sign-date"
            type="date"
            className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
            value={details.sign_date}
            onChange={(e) => setDetails({ ...details, sign_date: e.target.value })}
          />
        </Field>
        <Field label="Data rozpoczęcia" required>
          <input
            data-testid="input-start-date"
            type="date"
            className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
            value={details.start_date}
            onChange={(e) => setDetails({ ...details, start_date: e.target.value })}
          />
        </Field>
        <Field label="Data zakończenia" required={!isPerPackage}>
          <input
            data-testid="input-end-date"
            type="date"
            className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
            value={details.end_date}
            onChange={(e) => setDetails({ ...details, end_date: e.target.value })}
          />
          {isPerPackage && (
            <span className="text-[10px] text-zinc-400 font-mono">
              Puste = umowa na czas nieokreślony
            </span>
          )}
        </Field>
        <Field label="Stanowisko">
          <input
            data-testid="input-detail-position"
            className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-[#E52C34]"
            value={details.position}
            onChange={(e) => setDetails({ ...details, position: e.target.value })}
          />
        </Field>
        {!isPerPackage && (
          <Field label="Stawka godzinowa (zł brutto)" required>
            <input
              data-testid="input-detail-hourly-rate"
              type="number"
              step="0.01"
              className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
              value={details.hourly_rate}
              onChange={(e) => setDetails({ ...details, hourly_rate: e.target.value })}
            />
          </Field>
        )}
        {isPerPackage && (
          <>
            <Field label="Stawka za paczkę (zł brutto)" required>
              <div className="flex gap-2 items-stretch">
                <input
                  data-testid="input-package-rate"
                  type="number"
                  step="0.01"
                  min="0"
                  className="flex-1 border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
                  value={details.package_rate}
                  onChange={(e) => setDetails({ ...details, package_rate: e.target.value })}
                  placeholder={globalRates?.env_rate ?? "np. 5.00"}
                />
                {globalRates?.env_rate != null && (
                  <button
                    type="button"
                    onClick={() => setDetails({ ...details, package_rate: String(globalRates.env_rate) })}
                    data-testid="btn-use-global-rate"
                    className="px-3 py-2 text-xs font-bold uppercase tracking-wider border border-zinc-900 bg-white hover:bg-zinc-100"
                    title="Wstaw stawkę globalną z /admin/stawki"
                  >
                    Ze stawek
                  </button>
                )}
              </div>
              <span className="text-[10px] text-zinc-500 font-mono">
                Globalna stawka z <b>/admin/stawki</b>:{" "}
                {globalRates?.env_rate != null
                  ? `${Number(globalRates.env_rate).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł/paczka`
                  : "—"}
              </span>
            </Field>
            <Field label="Stawka za paczkę multipack (zł)">
              <input
                data-testid="input-multipack-extra-rate"
                type="number"
                step="0.01"
                min="0"
                className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
                value={details.multipack_extra_rate}
                onChange={(e) => setDetails({ ...details, multipack_extra_rate: e.target.value })}
                placeholder={String(globalRates?.multipack_extra_rate ?? 0.5)}
              />
              <span className="text-[10px] text-zinc-500 font-mono">
                Domyślnie {Number(globalRates?.multipack_extra_rate ?? 0.5).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł za każdą kolejną paczkę pod ten sam adres
              </span>
            </Field>
          </>
        )}
      </div>

      {isPerPackage && details.package_rate && (
        <div className="border-2 border-brand bg-gradient-to-br from-red-50 to-white p-6 rounded-xl" data-testid="package-preview">
          <div className="font-label text-brand mb-3">// Przykład rozliczenia</div>
          {(() => {
            const x = Number(details.package_rate) || 0;
            const ex = Number(details.multipack_extra_rate || globalRates?.multipack_extra_rate || 0.5);
            const fmt = (n) => `${n.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł`;
            return (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm">
                <div className="p-3 rounded-lg bg-white ring-1 ring-zinc-200">
                  <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">1 paczka pod adresem</div>
                  <div className="font-heading font-black text-xl mt-1">{fmt(x)}</div>
                </div>
                <div className="p-3 rounded-lg bg-white ring-1 ring-zinc-200">
                  <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">2 paczki pod tym samym adresem</div>
                  <div className="font-heading font-black text-xl mt-1">{fmt(x + ex)}</div>
                  <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{fmt(x)} + {fmt(ex)}</div>
                </div>
                <div className="p-3 rounded-lg bg-white ring-1 ring-zinc-200">
                  <div className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest">3 paczki pod tym samym adresem</div>
                  <div className="font-heading font-black text-xl mt-1">{fmt(x + 2 * ex)}</div>
                  <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{fmt(x)} + {fmt(ex)} + {fmt(ex)}</div>
                </div>
              </div>
            );
          })()}
          <div className="text-xs text-zinc-500 mt-4">
            Ta sama logika obowiązuje w portfelu kuriera — 1× pełna stawka + każda kolejna pod ten sam adres po stawce multipack.
          </div>
        </div>
      )}

      {isVehicle && (
        <div className="border border-zinc-900 p-8 space-y-6" data-testid="vehicle-section">
          <div className="flex items-center gap-3">
            <Car className="w-5 h-5 text-[#E52C34]" />
            <h3 className="font-heading font-bold text-lg uppercase">Dane pojazdu</h3>
          </div>

          {fleet && fleet.length > 0 && (
            <div className="border border-zinc-300 bg-zinc-50 p-4">
              <span className="font-label block mb-3">Wybierz pojazd z floty</span>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => setSelectedFleetId("")}
                  data-testid="fleet-pick-manual"
                  className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border ${
                    !selectedFleetId
                      ? "bg-zinc-900 text-white border-zinc-900"
                      : "bg-white border-zinc-300 hover:border-zinc-900"
                  }`}
                >
                  Wpisz ręcznie
                </button>
                {fleet.map((v) => {
                  const active = selectedFleetId === v.id;
                  return (
                    <button
                      key={v.id}
                      type="button"
                      onClick={() => setSelectedFleetId(v.id)}
                      data-testid={`fleet-pick-${v.id}`}
                      className={`px-4 py-2 text-xs font-bold uppercase tracking-wider border ${
                        active
                          ? "bg-[#E52C34] text-white border-[#E52C34]"
                          : "bg-white border-zinc-300 hover:border-zinc-900"
                      }`}
                    >
                      {v.brand} {v.model} · {v.registration}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <Field label="Marka">
              <input
                data-testid="vehicle-brand"
                className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-[#E52C34]"
                value={vehicle.brand}
                onChange={(e) => setVehicle({ ...vehicle, brand: e.target.value })}
              />
            </Field>
            <Field label="Model">
              <input
                data-testid="vehicle-model"
                className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-[#E52C34]"
                value={vehicle.model}
                onChange={(e) => setVehicle({ ...vehicle, model: e.target.value })}
              />
            </Field>
            <Field label="Rok produkcji">
              <input
                data-testid="vehicle-year"
                type="number"
                className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono focus:outline-none focus:border-[#E52C34]"
                value={vehicle.year}
                onChange={(e) => setVehicle({ ...vehicle, year: e.target.value })}
              />
            </Field>
            <Field label="Nr rejestracyjny">
              <input
                data-testid="vehicle-registration"
                className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono uppercase focus:outline-none focus:border-[#E52C34]"
                value={vehicle.registration}
                onChange={(e) => setVehicle({ ...vehicle, registration: e.target.value.toUpperCase() })}
              />
            </Field>
            <Field label="VIN" className="md:col-span-2">
              <input
                data-testid="vehicle-vin"
                className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 font-mono uppercase focus:outline-none focus:border-[#E52C34]"
                value={vehicle.vin}
                maxLength={17}
                onChange={(e) => setVehicle({ ...vehicle, vin: e.target.value.toUpperCase() })}
              />
            </Field>
          </div>

          <Field label="Uszkodzenia / uwagi w momencie przekazania">
            <textarea
              data-testid="vehicle-damages"
              rows={4}
              className="w-full border border-zinc-300 bg-zinc-50 px-3 py-2 focus:outline-none focus:border-[#E52C34]"
              value={vehicle.damages}
              onChange={(e) => setVehicle({ ...vehicle, damages: e.target.value })}
              placeholder="Opisz wszystkie istniejące uszkodzenia, rysy, wgniecenia itp."
            />
          </Field>

          <div>
            <span className="font-label block mb-3">Wyposażenie przekazane wraz z pojazdem</span>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(equipmentLabels).map(([key, label]) => {
                const checked = !!vehicle.equipment[key];
                return (
                  <label
                    key={key}
                    data-testid={`equip-${key}`}
                    className={`flex items-center gap-3 px-4 py-3 cursor-pointer border ${
                      checked
                        ? "border-[#E52C34] bg-[#E52C34]/5"
                        : "border-zinc-300 bg-zinc-50 hover:border-zinc-900"
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="w-5 h-5 accent-[#E52C34]"
                      checked={checked}
                      onChange={(e) =>
                        setVehicle({
                          ...vehicle,
                          equipment: { ...vehicle.equipment, [key]: e.target.checked },
                        })
                      }
                    />
                    <span className="text-sm font-medium">{label}</span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <NavButtons onBack={onBack} onNext={onNext} canProceed={canProceed} nextId="btn-next-3" />
    </section>
  );
}

function StepReview({
  contractType, employee, details, vehicle, isVehicle, isHours, equipmentLabels,
  onBack, onSubmit, submitting,
}) {
  const tpl = TEMPLATES.find((t) => t.id === contractType);
  const rate = Number(details.hourly_rate) || 0;
  const earned = Number(details.earned_amount) || 0;
  const computedHours = rate > 0 ? earned / rate : 0;
  const isPerPackage = contractType === "mandate_per_package";
  const pkgRate = Number(details.package_rate) || 0;
  const pkgExtra = Number(details.multipack_extra_rate || 0.5) || 0.5;
  return (
    <section className="space-y-6">
      <h2 className="font-heading font-bold text-xl uppercase">Podsumowanie</h2>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-zinc-900">
        <SummaryCard label="Wzór dokumentu" value={tpl?.title} testId="sum-template" />
        <SummaryCard
          label="Pracownik"
          value={employee ? `${employee.first_name} ${employee.last_name}` : "—"}
          subValue={employee && (
            <div className="space-y-0.5">
              {employee.pesel && <div>PESEL {employee.pesel}</div>}
              {employee.birth_date && (
                <div>ur. {new Date(employee.birth_date).toLocaleDateString("pl-PL")}</div>
              )}
              {(employee.id_document_series || employee.id_document_number) && (
                <div>
                  {ID_DOC_SHORT[employee.id_document_type] || "dok."}{" "}
                  {employee.id_document_series && `${employee.id_document_series} `}
                  {employee.id_document_number}
                </div>
              )}
            </div>
          )}
          testId="sum-employee"
          dark
        />
        <SummaryCard
          label="Stawka"
          value={
            isPerPackage
              ? (pkgRate > 0
                  ? `${pkgRate.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł / paczka`
                  : "—")
              : (details.hourly_rate
                  ? `${Number(details.hourly_rate).toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł / h`
                  : "—")
          }
          subValue={
            isPerPackage
              ? <>+ {pkgExtra.toLocaleString("pl-PL", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} zł / paczka multipack</>
              : (isHours ? null : details.position)
          }
          testId="sum-rate"
        />
      </div>

      {isHours ? (
        <div className="grid grid-cols-3 gap-0 border border-zinc-900" data-testid="sum-hours">
          <SummaryCard label="Miesiąc" value={details.period_month || "—"} testId="sum-period" />
          <SummaryCard label="Zarobiona kwota" value={earned ? `${earned.toFixed(2)} zł` : "—"} testId="sum-earned" dark />
          <SummaryCard label="Liczba godzin" value={computedHours > 0 ? `${computedHours.toFixed(2)} h` : "—"} testId="sum-hours-value" />
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-0 border border-zinc-900">
          <SummaryCard label="Data rozpoczęcia" value={details.start_date} testId="sum-start" />
          <SummaryCard label="Data zakończenia" value={details.end_date} testId="sum-end" dark />
        </div>
      )}

      {isVehicle && (
        <div className="border border-zinc-900 p-6 space-y-3" data-testid="sum-vehicle">
          <div className="font-label">Pojazd</div>
          <div className="font-heading font-bold text-xl">
            {vehicle.brand} {vehicle.model} {vehicle.year && `(${vehicle.year})`}
          </div>
          <div className="text-sm font-mono text-zinc-600">
            Nr rej: {vehicle.registration || "—"} • VIN: {vehicle.vin || "—"}
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            {Object.entries(equipmentLabels).map(([key, label]) =>
              vehicle.equipment[key] ? (
                <span
                  key={key}
                  className="text-xs px-3 py-1 bg-zinc-900 text-white uppercase tracking-wider font-bold"
                >
                  {label}
                </span>
              ) : null
            )}
          </div>
        </div>
      )}

      <div className="bg-zinc-50 border border-zinc-300 p-6">
        <div className="font-label mb-2">Pracodawca</div>
        <div className="font-semibold">GOPOSSIBLE SP. Z O.O.</div>
        <div className="text-sm text-zinc-600 font-mono mt-1">
          ul. Cyryla i Metodego 13/14, 71-541 Szczecin · NIP 8513329264
        </div>
      </div>

      <div className="flex justify-between items-center pt-2">
        <button
          onClick={onBack}
          data-testid="btn-back-review"
          className="inline-flex items-center gap-2 px-6 py-3 font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900"
        >
          <ArrowLeft className="w-4 h-4" /> Wstecz
        </button>
        <button
          onClick={onSubmit}
          disabled={submitting}
          data-testid="btn-generate-contract"
          className="inline-flex items-center gap-2 bg-[#E52C34] text-white px-8 py-3 font-bold uppercase tracking-wider hover:bg-[#C5232A] disabled:bg-zinc-400 transition-colors"
        >
          {submitting ? "Generowanie..." : (<><Check className="w-4 h-4" /> Wygeneruj umowę</>)}
        </button>
      </div>
    </section>
  );
}

function StepDone({ contract, onAnother, onHistory }) {
  return (
    <section className="space-y-8" data-testid="step-done">
      <div className="border border-zinc-900 bg-[#E52C34] text-white p-10">
        <div className="font-label text-white/70 mb-3">// Sukces</div>
        <h2 className="font-heading font-black text-3xl uppercase">
          Dokument został wygenerowany
        </h2>
        <p className="mt-4 text-white/80 max-w-xl">
          Numer dokumentu <span className="font-mono font-bold">{contract.contract_number}</span>.
          Pobierz w wybranym formacie lub podejrzyj w przeglądarce.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to={`/contracts/${contract.id}`}
            data-testid="btn-view-contract"
            className="inline-flex items-center gap-2 bg-white text-zinc-900 px-6 py-3 font-bold uppercase tracking-wider hover:bg-zinc-100"
          >
            <Eye className="w-4 h-4" /> Podgląd i druk
          </Link>
          <button
            onClick={async () => {
              try {
                await Contracts.download(contract.id, "pdf");
                toast.success("Pobrano PDF");
              } catch (e) {
                toast.error("Błąd pobierania PDF");
              }
            }}
            data-testid="btn-download-pdf"
            className="inline-flex items-center gap-2 bg-zinc-900 text-white border border-white px-6 py-3 font-bold uppercase tracking-wider hover:bg-zinc-800"
          >
            <Download className="w-4 h-4" /> Pobierz PDF
          </button>
          <button
            onClick={async () => {
              try {
                await Contracts.download(contract.id, "docx");
                toast.success("Pobrano DOCX");
              } catch (e) {
                toast.error("Błąd pobierania DOCX");
              }
            }}
            data-testid="btn-download-docx"
            className="inline-flex items-center gap-2 bg-zinc-900 text-white border border-white px-6 py-3 font-bold uppercase tracking-wider hover:bg-zinc-800"
          >
            <Download className="w-4 h-4" /> Pobierz DOCX
          </button>
        </div>
      </div>
      <div className="flex gap-3">
        <button
          onClick={onAnother}
          data-testid="btn-another-contract"
          className="px-6 py-3 font-bold uppercase tracking-wider border border-zinc-900 hover:bg-zinc-900 hover:text-white"
        >
          Kolejna umowa
        </button>
        <button
          onClick={onHistory}
          data-testid="btn-go-history"
          className="px-6 py-3 font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900"
        >
          Zobacz historię
        </button>
      </div>
    </section>
  );
}

function NavButtons({ onBack, onNext, canProceed, nextId }) {
  return (
    <div className="flex justify-between pt-2">
      <button
        onClick={onBack}
        data-testid="btn-back"
        className="inline-flex items-center gap-2 px-6 py-3 font-bold uppercase tracking-wider border border-zinc-300 hover:border-zinc-900"
      >
        <ArrowLeft className="w-4 h-4" /> Wstecz
      </button>
      <button
        onClick={onNext}
        disabled={!canProceed}
        data-testid={nextId}
        className="inline-flex items-center gap-2 bg-[#E52C34] text-white px-6 py-3 font-bold uppercase tracking-wider hover:bg-[#C5232A] disabled:bg-zinc-300 disabled:cursor-not-allowed transition-colors"
      >
        Dalej <ArrowRight className="w-4 h-4" />
      </button>
    </div>
  );
}

function Field({ label, required, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="font-label block mb-2">
        {label}
        {required && <span className="text-[#E52C34] ml-1">*</span>}
      </span>
      {children}
    </label>
  );
}

function SummaryCard({ label, value, subValue, testId, dark }) {
  return (
    <div
      data-testid={testId}
      className={`p-6 border-r border-zinc-900 last:border-r-0 ${
        dark ? "bg-zinc-900 text-white" : "bg-white"
      }`}
    >
      <div className={`font-label ${dark ? "text-zinc-400" : "text-zinc-500"}`}>{label}</div>
      <div className="font-heading font-bold text-lg mt-3">{value || "—"}</div>
      {subValue && (
        <div className={`text-xs mt-1 ${dark ? "text-zinc-400" : "text-zinc-500"} font-mono`}>
          {subValue}
        </div>
      )}
    </div>
  );
}
